import { z } from 'zod'

import {
  accountFormDefaultValues,
  ZAccountFormValues,
} from '@/features/update-account/@x/sign-up'
import {
  receiptInformationDefaultValues,
  ReceiptInformationValidation,
  ZReceiptInformationFormValues,
} from '@/features/update-receipt-information/@x/sign-up'
import {
  TPassportVerificationForm,
  TPhoneVerificationForm,
} from '@/features/verify-identity/@x/sign-up'

export const ZTermsForm = z.object({
  termsOfService: z.boolean(),
  privacyConsent: z.boolean(),
  marketingAgree: z.boolean(),
  ageOver19: z.boolean(),
})

export const ZInfoForm = z.object({
  email: z
    .string()
    .min(1, '이메일 주소를 입력해 주세요.')
    .email('이메일 형태로 입력해 주세요.'),
  name: z
    .string()
    .min(1, '이름을 입력해 주세요.')
    .max(10, '이름은 10자 이하로 입력해 주세요.'),
  password: z
    .string()
    .min(1, '비밀번호를 입력해 주세요.')
    .regex(/^(?=.*[a-zA-Z])(?=.*\d).{6,15}$/, {
      message: '영문, 숫자 조합 6자-15자로 입력해 주세요.',
    }),
  passwordConfirm: z.string().min(1, '비밀번호를 확인해 주세요.'),
  profileImageUrl: z.string(),
})

const withPasswordConfirm = <
  T extends z.ZodObject<{
    password: z.ZodString
    passwordConfirm: z.ZodString
  }>,
>(
  schema: T,
): z.ZodType<z.infer<T>> =>
  schema.refine(data => data.password === data.passwordConfirm, {
    message: '비밀번호가 일치하지 않습니다.',
    path: ['passwordConfirm'],
  })

export const ZGuestInfoForm = withPasswordConfirm(ZInfoForm)

export const ZHostInfoForm = withPasswordConfirm(
  ZInfoForm.extend({
    phoneNumber: z.string(),
    certNum: z.string(),
    hostIntro: z.string().min(1, '호스트 소개를 입력해 주세요.'),
  }),
)

export const ZHostPayForm = ZAccountFormValues.pick({
  accountHolder: true,
})
  .merge(ZReceiptInformationFormValues)
  .extend({
    bankName: z.string().trim().min(1, {
      message: '정산 받으실 은행을 선택해 주세요.',
    }),
    bankAccount: z.string().trim().min(1, {
      message: '정산 받으실 계좌번호를 입력해 주세요.',
    }),
    recommenderPhoneNumber: z.string().optional(),
  })
  .superRefine(ReceiptInformationValidation)

export const termsFormDefaultValues = {
  termsOfService: false,
  privacyConsent: false,
  marketingAgree: false,
  ageOver19: false,
}

export const guestInfoFormDefaultValues = {
  email: '',
  name: '',
  password: '',
  passwordConfirm: '',
  profileImageUrl: '',
}

export const guestVerificationFormDefaultValues = {
  contactNumber: '',
  passportImageUrl: '',
  phoneNumber: '',
  certNum: '',
}

export const hostInfoFormDefaultValues = {
  ...guestInfoFormDefaultValues,
  phoneNumber: '',
  certNum: '',
  hostIntro: '',
}

export const hostPayFormDefaultValues: THostPayForm = {
  ...accountFormDefaultValues,
  ...receiptInformationDefaultValues,
  recommenderPhoneNumber: '',
}

export type TTermsForm = z.infer<typeof ZTermsForm>

export type TGuestInfoForm = z.infer<typeof ZGuestInfoForm>
export type THostInfoForm = z.infer<typeof ZHostInfoForm>

export type TVerificationData = Partial<
  TPhoneVerificationForm & TPassportVerificationForm
>

export type THostPayForm = z.infer<typeof ZHostPayForm>
